import apiClient from './api_client';

/* ------------------------------------------------------------
 * Microsoft capabilities: the Graph scopes each one needs, and whether this
 * session's token carries them.
 *
 * Nothing about a tenant is stored: a Graph token carries every delegated scope
 * consented for that resource on behalf of the signed-in user, so it answers
 * live where a stored copy could only go stale against Azure.
 * ------------------------------------------------------------ */

export enum MicrosoftCapability {
    OUTLOOK_MAIL = 'outlook_mail',
    TEAMS_MESSAGES = 'teams_messages',
    TEAMS_MEETINGS = 'teams_meetings'
}

export enum ConsentStatus {
    NOT_GRANTED = 'not_granted',
    /** Consented to once, and the capability has grown a scope since. */
    INCOMPLETE = 'incomplete',
    GRANTED = 'granted'
}

/** Served rather than duplicated here: the same map gates the tools server-side. */
export type CapabilityDefinition = {
    capability: MicrosoftCapability;
    required: string[];
};

export type CapabilityState = CapabilityDefinition & {
    granted: string[];
    missing: string[];
    status: ConsentStatus;
};

const GRAPH = 'https://graph.microsoft.com';
/** Survives the round trip to Microsoft; scoped to the tab that started it. */
const PENDING_KEY = 'microsoft-consent-pending';

export const listMicrosoftCapabilities = async (
    tenantId: string
): Promise<CapabilityDefinition[]> => {
    const response = await apiClient.get(
        `/microsoft/capabilities?tenant_id=${tenantId}`
    );
    return response.json();
};

/** `https://graph.microsoft.com/Mail.ReadWrite` and `mail.readwrite` name the same scope. */
function scopeName(scope: string): string {
    return (scope.split('/').pop() ?? scope).toLowerCase();
}

/**
 * Where a capability stands for this session.
 *
 * `INCOMPLETE` is what keeps a capability that grew a scope from being lost: the tenant keeps
 * what it consented to, and is offered only the difference.
 */
export function capabilityState(
    definition: CapabilityDefinition,
    tokenScopes: string[]
): CapabilityState {
    const held = new Set(tokenScopes.map(scopeName));
    const granted = definition.required.filter((scope) =>
        held.has(scopeName(scope))
    );
    const missing = definition.required.filter(
        (scope) => !held.has(scopeName(scope))
    );

    let status = ConsentStatus.NOT_GRANTED;
    if (!missing.length) status = ConsentStatus.GRANTED;
    else if (granted.length) status = ConsentStatus.INCOMPLETE;

    return { ...definition, granted, missing, status };
}

/**
 * The only place a grant can be revoked -- nothing in this application can.
 *
 * A consent given through our button lands there under "Other permissions granted", below the
 * configured ones: the admin consent endpoint grants a scope without declaring it on the app.
 */
export function entraPermissionsUrl(clientId: string): string {
    return `https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/CallAnAPI/appId/${clientId}`;
}

/** Send the admin to Microsoft's consent screen for `scopes` — what is missing, not the whole
 *  capability, so the screen asks for what is actually being added. */
export function openAdminConsent(
    capability: MicrosoftCapability,
    scopes: string[],
    clientId: string,
    azureTenantId: string
): void {
    const state = crypto.randomUUID();
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ state, capability }));

    const url = new URL(
        `https://login.microsoftonline.com/${azureTenantId}/v2.0/adminconsent`
    );
    url.searchParams.set('client_id', clientId);
    url.searchParams.set(
        'scope',
        scopes.map((scope) => `${GRAPH}/${scope}`).join(' ')
    );
    // Entra matches registered redirect URIs whole, and this origin is the one MSAL signs in
    // against: a path or a query string of our own is rejected before the screen is shown.
    url.searchParams.set('redirect_uri', `${window.location.origin}/`);
    url.searchParams.set('state', state);

    window.location.assign(url.toString());
}

export type ConsentAnswer =
    | { granted: MicrosoftCapability }
    /** Microsoft's own wording: an admin here is not always an admin of the tenant. */
    | { error: string };

let answer: ConsentAnswer | null = null;

/**
 * Read Microsoft's consent answer off the current URL. Must run before the app mounts, while
 * the URL is still the one Microsoft sent us to; `microsoftConsentGuard` then clears it.
 *
 * The `state` this tab sent is what identifies the answer: a refusal carries no
 * `admin_consent` at all, and `error` alone belongs to the MSAL and Descope callbacks.
 */
export function captureAdminConsentFromUrl(
    search: string = window.location.search
): void {
    const pending = sessionStorage.getItem(PENDING_KEY);
    if (!pending) return;

    let state: string;
    let capability: MicrosoftCapability;
    try {
        ({ state, capability } = JSON.parse(pending));
    } catch {
        // Runs before the app mounts, so anything thrown here is a blank page.
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    const params = new URLSearchParams(search);
    // Left in place when it is not ours: the real answer may still be on its way back. And
    // left in place when it is, until the screen has acted on it -- `answer` lives in memory,
    // so a reload before then would otherwise lose a consent that did happen, along with the
    // guard's only reason to clean the URL.
    if (state !== params.get('state')) return;

    answer =
        params.get('admin_consent') === 'True'
            ? { granted: capability }
            : {
                  error:
                      params.get('error_description') ??
                      params.get('error') ??
                      'Microsoft did not grant the permission.'
              };
}

/** What Microsoft answered, until the screen has acted on it. */
export function pendingConsentAnswer(): ConsentAnswer | null {
    return answer;
}

/** Acted on: drop it from this page and from the store a reload would read it back from. */
export function clearConsentAnswer(): void {
    answer = null;
    sessionStorage.removeItem(PENDING_KEY);
}
